<template>
  <div
    class="effect-cover__selection"
    :style="selectedEffectStyle"
    @mousedown="handleMouseDown"
    @dblclick="handleDblClick"
    ref="selection"
  >
    <template v-for="vd in ['t', 'b']">
      <div
        v-for="hd in ['r', 'l']"
        :key="vd + hd"
        :class="['effect-cover__handle', 'effect-cover__handle--' + vd + hd]"
        :style="{ cursor: getResizeCursor(vd + hd) }"
        @mousedown="handleMouseDown($event, vd + hd)"
      />
    </template>
    <div class="effect-cover__rotate" @mousedown="handleRotateMouseDown" />
    <textarea
      class="effect-cover__input"
      v-if="showInput"
      v-model="selectedEffect.text"
      :style="editorStyle"
      @mousedown.stop
      @contextmenu.stop
      @input="handleInput"
      @blur="handleBlur"
    />
  </div>
</template>
<script>
import { getEffectBoxStyle, isMac, rotateBy } from '../../utils';
import { adjustSize } from '../../utils/detectLines';

const between = (val, start, end) => (val - start) * (val - end) <= 0;
const betweenDelta = (val, start, delta) => between(val, start, start + delta);

export default {
  props: ['selectedEffect', 'nextEffects', 'dragging', 'scale', 'fontStates'],
  data() {
    return {
      showInput: false
    }
  },
  computed: {
    selectedEffectStyle() {
      if (this.selectedEffect) {
        if (!this.selectedEffect.locked) {
          let ret = getEffectBoxStyle({ content: this.selectedEffect });

          ['left', 'top', 'width', 'height'].forEach(key => {
            ret[key] = parseFloat(ret[key]) * this.scale + 'px';
          });
          return ret;
        }
      }
      return {
        display: 'none'
      };
    },

    editorStyle() {
      const ret = {
        background: 'rgba(255,255,255,.3)'
      };
      const efc = this.selectedEffect;
      const fontSize = efc.fontSize;
      ret.fontSize = fontSize + 'px';
      ret.lineHeight = efc.lineHeight;
      ret.textAlign = efc.align;
      if (fontSize < 12) {
        ret.fontSize = '12px';
        ret.transform = `translate(-50%,-50%) scale(${fontSize / 12 * this.scale}) translate(50%,50%)`;
        ret.width = 1200 / this.scale / fontSize + '%';
        ret.height = 1200 / this.scale / fontSize + '%';
      } else {
        ret.transform = `translate(-50%,-50%) scale(${this.scale}) translate(50%,50%)`;
        ret.width = 100 / this.scale + '%';
        ret.height = 100 / this.scale + '%';
      }

      if (efc.shadow) {
        ret.textShadow = `${efc.shadowX}px ${efc.shadowY}px ${efc.shadowBlur}px ${efc.shadowColor}`;
      }

      ret.color = efc.color;
      ret.fontFamily = efc.fontFamily + ",SourceHanSansCN-Regular,sans-serif";
      ret.letterSpacing = (efc.letterSpacing / 100 || 0) + 'em';

      return ret;
    },
    adjustFontSizeMarker() {
      const effect = this.selectedEffect;
      if (effect && effect.autoFontSize) {
        return [
          effect.fontFamily,
          effect.lineHeight,
          effect.width,
          effect.height,
          effect.letterSpacing,
          this.fontStates[effect.fontFamily]
        ].join(',');
      }
      return '';
    },
    computedDragging: {
      get() {
        return this.dragging;
      },
      set(value) {
        this.$emit('update:dragging', value);
      }
    }
  },
  methods: {
    getResizeCursor(dir) {
      if (this.selectedEffect) {
        let rotation = this.selectedEffect.rotate;

        switch (dir) {
          case 'tl':
          case 'br':
            rotation += 45;
            break;
          default:
            rotation += 135;
        }

        switch (Math.round((rotation + 180) % 180 / 45)) {
          case 0:
          case 4:
            return 'ew-resize';
          case 1:
            return 'nwse-resize';
          case 2:
            return 'ns-resize';
          case 3:
            return 'nesw-resize';
        }
      }
      return 'default';
    },
    handleMouseDown(e, dir) {
      if (!e.button && e.target.tagName.toLowerCase() !== 'input') {
        const bcr = this.$parent.$el.getBoundingClientRect();
        let selectNext = false;

        const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

        if (dir) {
          e.stopPropagation();
        } else {
          // let afterCurrent = false;
          let nextIndex = null;

          selectNext = this.nextEffects.some((ef, i) => {
            // TODO: rotation-aware hit test
            const center = {
              x: ef.left + ef.width / 2 + bcr.left / this.scale,
              y: ef.top + ef.height / 2 + bcr.top / this.scale
            };
            const unrotatedMouse = rotateBy({
              x: e.clientX / this.scale,
              y: e.clientY / this.scale
            }, center, -ef.rotate || 0);

            if (betweenDelta(unrotatedMouse.x, ef.left + bcr.left / this.scale, ef.width)
              && betweenDelta(unrotatedMouse.y, ef.top + bcr.top / this.scale, ef.height)
            ) {
              nextIndex = i;
              return true;
            }
            return false;
          });
          if (selectNext) {
            // handle selection
            this.$emit('selectCover', { event: e, index: nextIndex });
            return;
          }
        }
        const obj = {
          clientX: e.clientX,
          clientY: e.clientY,
          dir,
          effect: this.selectedEffect,
          ctrlKey: !selectNext && ctrlKey
        };
        this.computedDragging = obj;
      }
    },
    handleRotateMouseDown(e) {
      if (!e.button) {
        e.stopPropagation();
        const bcr = this.$refs.selection.getBoundingClientRect();
        this.computedDragging = {
          clientX: e.clientX,
          clientY: e.clientY,
          effect: this.selectedEffect,
          rotate: [
            bcr.left + bcr.width / 2,
            bcr.top + bcr.height / 2
          ]
        };
      }
    },
    handleDblClick() {
      if (!this.selectedEffect.type) {
        this.showInput = true;
      }
    },
    handleInput(e) {
      const effect = this.selectedEffect;
      if (effect.autoFontSize) {
        if (!this.adjustPending) {
          this.adjustPending = setTimeout(() => {
            this.adjustPending = false;
            this.adjustFontSize(effect);
          }, 1e3);
        }
      }
    },
    handleBlur() {
      const effect = this.selectedEffect;
      if (effect && this.adjustPending) {
        console.log('handling blur');
        clearTimeout(this.adjustPending);
        this.adjustPending = false;
        this.adjustFontSize(effect);
      }

      console.log('clearing measuring effect');

      setTimeout(() => {
        if (this.showInput) {
          console.log('stop text editing');
          this.showInput = false;
        }
      }, 50);
    },
    async adjustFontSize(effect) {
      if (effect.type) {
        return;
      }
      const fontSize = await adjustSize(effect, 1);
      effect.fontSize = Math.max(fontSize, 1);
    }
  },
  watch: {
    adjustFontSizeMarker(val, prev) {
      console.log('excuting watcher: adjustFontSizeMarker');
      if (val) {
        const effect = this.selectedEffect;
        const fontFamily = val.split(',')[0], prevFontFamily = prev.split(',')[0];
        if (!this.adjustFontSizeLock) {
          this.adjustFontSizeLock = true;
          setTimeout(() => {
            console.log('timeout was met');
            this.adjustFontSizeLock = false;
            this.adjustFontSize(effect);
            if (prevFontFamily && fontFamily !== prevFontFamily) {
              setTimeout(() => {
                this.adjustFontSize(effect);
              }, 1e3);
            }
          }, 60);
        }
      }
    },
    selectedEffect(val) {
      if (!val) {
        this.showInput = false;
      }
    },
    computedDragging(val, prev) {
      if (!val && prev.dir) {
        // TODO:
        console.log('dragging was stopped');
        if (this.selectedEffect.autoFontSize) {
          this.adjustFontSize(this.selectedEffect);
        }
      }
    }
  },
  beforeDestroy() {
    this.showInput = false;
  }
}
</script>